import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchLocations, ILocation } from '../../../libraries/location';
import { AppState } from '../../client-redux/reducers';
import Header from '../../components/util/header';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import LocationsForm from '../../components/location-edit/locations-form';
import { Grid, Group, Table, Loader } from '@mantine/core';
import { IEntity } from '../../../libraries/entity';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import './styles.scss';

enum MODE {
  DEFAULT = 'default'
}

const Locations = () => {
  const lang = useSelector((state: AppState) => state.lang.lang);
  const currencyList = useSelector((state: AppState) => state.currencies.currencies);
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);

  const [currencies] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<ILocation[]>([]);

  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData) {
      loadLocationsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
  };

  const loadLocationsList = () => {
    if (!filterBarData) {
      return;
    }

    setLoading(true);
    setList([]);

    fetchLocations(currencies, filterBarData.entity || entities[0].entid || 0)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.shops || []);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="locals">
      <Header pageTitle={lang.GLOBAL_LOCATIONS} />
      <FilterBar
        onSearch={loadLocationsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{ entity: true }}
      />
      <Grid gap="xl">
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Group className="locals-table">
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Tbody>
                {loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={16}>
                      <Loader color="#016273" />
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  <>
                    {!list?.length ? (
                      <Table.Tr>
                        <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                          {lang.GLOBAL_NO_RESULTS}
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      list?.map((elem, index) => {
                        return (
                          <Table.Tr
                            key={index}
                            style={{
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSelectedLocation(elem);
                            }}
                          >
                            <Table.Td>{`${elem.sid} - ${elem.name}`}</Table.Td>
                          </Table.Tr>
                        );
                      })
                    )}
                  </>
                )}
              </Table.Tbody>
            </Table>
          </Group>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 5 }}>
          <LocationsForm
            lang={lang}
            location={selectedLocation || null}
            currencyList={currencyList}
            selectedEntity={filterBarData?.entity}
            loading={loading}
            mode={MODE.DEFAULT}
            workflow="locals"
            reloadLocations={loadLocationsList}
            clear={() => setSelectedLocation(null)}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
};
export default Locations;
